if (!customElements.get('sticky-atc')) {
  customElements.define(
    'sticky-atc',
    class StickyAtc extends HTMLElement {
      #variantChangeUnsubscriber = undefined;
      #mainButtonPassed = false;
      #footerVisible = false;
      #purchasable = true;
      #selectedTitle = '';
      #desktopQuery = window.matchMedia('(min-width: 750px)');

      /**
       * Pin the desktop bar under the sticky header, and flag the footer as
       * blocking once its top edge reaches the top of the bar's own band at
       * the bottom of the viewport.
       */
      #onScroll = () => {
        const headerBottom = this.header?.getBoundingClientRect().bottom ?? 0;
        this.style.setProperty('--sticky-atc-top', `${Math.max(0, headerBottom)}px`);

        if (this.footer) {
          const barHeight = this.offsetHeight || 64;
          const footerTop = this.footer.getBoundingClientRect().top;
          this.#footerVisible = footerTop <= window.innerHeight - barHeight;
        }

        // Measured on every scroll rather than observed: a fast fling back to the
        // top can skip the button without it ever intersecting the viewport. The
        // button counts as passed once it is behind the sticky header.
        if (this.mainButton) {
          this.#mainButtonPassed = this.mainButton.getBoundingClientRect().bottom <= Math.max(0, headerBottom);
        }

        this.#updateVisibility();
      };

      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.variantInput = this.querySelector('input[name="id"]');
        this.submitButton = this.querySelector('[data-sticky-submit]');
        this.submitButtonText = this.querySelector('[data-sticky-submit-text]');
        this.variantLabel = this.querySelector('[data-sticky-variant]');
        this.image = this.querySelector('[data-sticky-image]');
        this.price = this.querySelector('[data-sticky-price]');
        this.picker = this.querySelector('[data-sticky-picker]');
        this.#purchasable = !this.submitButton?.hasAttribute('disabled');

        this.#setupVisibility();
        this.#setupPicker();

        this.#variantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.variantChange,
          this.#onVariantChange.bind(this)
        );
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.#onScroll);
        window.removeEventListener('resize', this.#onScroll);
        this.#variantChangeUnsubscriber?.();
      }

      /**
       * Show the bar only once the main add-to-cart button has been scrolled past,
       * and while the footer is not on screen, so it never overlaps either the real
       * buy button or the footer. It stays hidden on first paint even when the
       * button sits below the fold: shown straight away, the bar sat under the
       * thumb as visitors from in-app browsers began to scroll, and Clarity showed
       * most mobile add-to-carts were a first tap within seconds of landing.
       */
      #setupVisibility() {
        this.mainButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
        this.header = document.querySelector('.section-header');
        this.footer = document.querySelector('.footer') || document.querySelector('footer');

        // Track the header's bottom edge (desktop), whether the real button has
        // been scrolled past, and hide the bar once the footer rises far enough
        // that it would sit under it (mobile). The footer is taller than the
        // viewport, so an IntersectionObserver on it reports "intersecting" long
        // before its top edge arrives; compare that top edge against the bar's
        // band instead. Without a main button the bar always stands in.
        if (!this.mainButton) this.#mainButtonPassed = true;
        window.addEventListener('scroll', this.#onScroll, { passive: true });
        window.addEventListener('resize', this.#onScroll, { passive: true });
        this.#onScroll();
      }

      #updateVisibility() {
        // Show once the real button has been scrolled past and the footer has not
        // yet risen into view. Sold out / coming soon variants hide the bar, unless
        // the desktop picker is there to switch back to an available one.
        const keepForPicker = Boolean(this.picker) && this.#desktopQuery.matches;
        this.hidden =
          (!this.#purchasable && !keepForPicker) || !this.#mainButtonPassed || this.#footerVisible;
      }

      /**
       * Forward picker choices to the matching input of the main variant
       * picker, which fetches the variant and publishes `variant-change`.
       * Hovering a swatch previews its value in the variant line.
       */
      #setupPicker() {
        if (!this.picker) return;

        this.picker.addEventListener('click', (event) => {
          const swatch = event.target.closest('button[data-sticky-option]');
          if (!swatch || swatch.getAttribute('aria-pressed') === 'true') return;
          this.#selectMainOption(swatch.dataset.stickyOption);
        });

        this.picker.addEventListener('change', (event) => {
          const option = event.target.selectedOptions?.[0];
          if (option) this.#selectMainOption(option.dataset.stickyOption);
        });

        this.picker.addEventListener('pointerover', (event) => {
          const swatch = event.target.closest('button[data-sticky-option]');
          if (swatch && this.variantLabel) this.variantLabel.textContent = swatch.dataset.value;
        });

        this.picker.addEventListener('pointerout', (event) => {
          if (this.picker.contains(event.relatedTarget)) return;
          if (this.variantLabel) this.variantLabel.textContent = this.#selectedTitle;
        });

        this.#selectedTitle = this.variantLabel?.textContent.trim() ?? '';
      }

      #selectMainOption(inputId) {
        const target = document.getElementById(inputId);
        if (!target) return;

        if (target instanceof HTMLOptionElement) {
          target.selected = true;
          target.parentElement.dispatchEvent(new Event('change', { bubbles: true }));
        } else {
          target.click();
        }
      }

      /**
       * Mirror the selected and unavailable state of the freshly rendered main
       * picker onto the sticky picker.
       */
      #syncPicker(html) {
        if (!this.picker) return;

        this.picker.querySelectorAll('button[data-sticky-option]').forEach((swatch) => {
          const input = html.getElementById(swatch.dataset.stickyOption);
          if (!input) return;
          swatch.setAttribute('aria-pressed', String(input.checked));
          swatch.classList.toggle('is-unavailable', input.classList.contains('disabled'));
        });

        this.picker.querySelectorAll('option[data-sticky-option]').forEach((option) => {
          const source = html.getElementById(option.dataset.stickyOption);
          if (!source) return;
          option.selected = source instanceof HTMLOptionElement ? source.selected : source.checked;
        });
      }

      /**
       * Sync the add-to-cart button with the variant selected on the main
       * product form: variant id and its sold out / unavailable state.
       * @param {{ data: { sectionId: string, html: Document, variant: object } }} payload
       */
      #onVariantChange({ data: { sectionId, html, variant } }) {
        if (sectionId !== this.sectionId) return;

        if (!variant) {
          this.#syncPicker(html);
          this.#setUnavailable();
          return;
        }

        this.#updateVariantInput(variant.id);
        this.#updateSubmitButton(html);
        this.#updateSummary(html, variant);
        this.#syncPicker(html);
      }

      /**
       * Refresh the desktop summary: variant title, image and price.
       */
      #updateSummary(html, variant) {
        this.#selectedTitle = variant.title;
        if (this.variantLabel) this.variantLabel.textContent = variant.title;

        const imageSrc = variant.featured_media?.preview_image?.src;
        if (this.image && imageSrc) {
          const url = new URL(imageSrc, window.location.origin);
          url.searchParams.set('width', '120');
          this.image.src = url.toString();
          this.image.removeAttribute('srcset');
        }

        const price = html.getElementById(`price-${this.sectionId}`)?.querySelector('.price');
        if (this.price && price) {
          price.classList.remove('price--large', 'price--show-badge');
          price.querySelectorAll('.badge').forEach((badge) => badge.remove());
          this.price.replaceChildren(price);
        }
      }

      #updateVariantInput(variantId) {
        if (!this.variantInput) return;
        this.variantInput.value = variantId ?? '';
        this.variantInput.disabled = false;
      }

      /**
       * Mirror the disabled/sold-out state of the main submit button so the
       * sticky button always reflects the real availability of the variant.
       */
      #updateSubmitButton(html) {
        const mainButton = html.getElementById(`ProductSubmitButton-${this.sectionId}`);
        const disabled = mainButton?.hasAttribute('disabled') ?? false;
        const label = mainButton?.querySelector('span')?.textContent.trim();

        this.submitButton.toggleAttribute('disabled', disabled);
        if (label) this.submitButtonText.textContent = label;
        this.variantInput.toggleAttribute('disabled', disabled);
        this.#purchasable = !disabled;
        this.#updateVisibility();
      }

      #setUnavailable() {
        this.submitButton.setAttribute('disabled', 'disabled');
        this.submitButtonText.textContent = window.variantStrings.unavailable;
        this.variantInput.setAttribute('disabled', 'disabled');
        this.#purchasable = false;
        this.#updateVisibility();
      }
    }
  );
}
